// Lightweight client-side notification system for PintaFlow.
// - In-app sonner toast + native browser notification (when granted)
// - Permission is requested in-context (e.g., on Goals page)
// - "Smart reminders" run once per session, throttled per day so users
//   never get spammed.

import { toast } from "sonner";

const PERM_ASKED_KEY = "pintaflow_notif_asked";
const LAST_REMINDER_KEY = "pintaflow_last_reminder";

export function notifPermissionState() {
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission; // 'default' | 'granted' | 'denied'
}

export function alreadyAsked() {
  try { return !!localStorage.getItem(PERM_ASKED_KEY); } catch { return false; }
}

export async function requestNotifPermission(reason = "default") {
  if (typeof Notification === "undefined") return "unsupported";
  if (Notification.permission === "granted") return "granted";
  try { localStorage.setItem(PERM_ASKED_KEY, reason); } catch { /* noop */ }
  try {
    const res = await Notification.requestPermission();
    return res;
  } catch { return "denied"; }
}

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function canRemindToday(key) {
  try {
    const raw = localStorage.getItem(LAST_REMINDER_KEY) || "{}";
    const map = JSON.parse(raw);
    return map[key] !== todayStr();
  } catch { return true; }
}

function markReminded(key) {
  try {
    const raw = localStorage.getItem(LAST_REMINDER_KEY) || "{}";
    const map = JSON.parse(raw);
    map[key] = todayStr();
    localStorage.setItem(LAST_REMINDER_KEY, JSON.stringify(map));
  } catch { /* noop */ }
}

// Send a notification (browser if granted, otherwise in-app toast).
export function notify({ key, title, description, action }) {
  if (key && !canRemindToday(key)) return; // throttle: max 1 per key per day

  if (typeof Notification !== "undefined" && Notification.permission === "granted") {
    try {
      const n = new Notification(title, { body: description, icon: "/favicon-32.png", tag: key });
      if (action) {
        n.onclick = () => {
          window.focus();
          if (action.onClick) action.onClick();
          n.close();
        };
      }
    } catch { /* noop */ }
  } else {
    // Browser permission denied / unsupported — fall back to in-app toast.
    toast(title, {
      description,
      duration: 8000,
      action,
    });
  }
  if (key) markReminded(key);
}

// Heuristic reminders. Call this once after user data loads on app open.
// `user.streak_count` + last activity date drive the streak-break logic.
export function runSmartReminders({ streak, lastActivityDate, goalDueIn }) {
  // 1) Streak in danger — user has a streak >= 2 but no activity today.
  if (streak >= 2 && lastActivityDate && lastActivityDate !== todayStr()) {
    notify({
      key: "streak-warning",
      title: `🔥 ${streak}-day streak in danger`,
      description: "Capture one idea or publish anything to keep it alive.",
      action: { label: "Open PintaFlow", onClick: () => { window.location.href = "/dashboard"; } },
    });
  }
  // 2) Goal deadline approaching — 3 days or fewer remaining.
  if (typeof goalDueIn === "number" && goalDueIn >= 0 && goalDueIn <= 3) {
    notify({
      key: "goal-deadline",
      title: `🎯 Goal deadline in ${goalDueIn} day${goalDueIn === 1 ? "" : "s"}`,
      description: "A small push today keeps the target real.",
      action: { label: "View Goals", onClick: () => { window.location.href = "/goals"; } },
    });
  }
}
